import {Component, Directive, forwardRef, Input} from "@angular/core";

/**
 * Directive selector to locate search actions within a wbo-actions-search-column
 */
@Directive({selector: "[searchAction]"})
export class SearchActionSelector {
}

/**
 * Interface that all Search Action Components should implement
 */
export interface SearchAction {

    /**
     * Provides an indication of importance so the actions can be sorted into a predictable order.
     * Larger importance values puts the action towards the front and lower importance towards the end.
     */
    getImportance(): number;


    /**
     * Retrieves the icon to use for the action
     * @param data Row data
     */
    getIcon(data: any): string;

    /**
     * Retrieves the icon class to use for the action
     * @param data Row data
     */
    getIconClass(data: any): string;

    /**
     * Retrieves the icon size to use for the action
     * @param data Row data
     */
    getIconSize(data: any): number;

    /**
     * Retrieves the name of the action which is used to derive an i18n key
     * @param data Row data
     */
    getName(data: any): string;

    /**
     * Invoked when the search action button is clicked
     * @param data Raw grid row data
     * @return Promise resolving to the results of the action
     */
    doAction(data: any): Promise<void>;

    /**
     * Determines availability of the search action.
     * @param data Raw grid row data
     * @return whether the search action should be made available
     */
    isAvailable(data: any): boolean;
}

/**
 * Generic Search Action Component. This SearchAction should not be used directly. Specific SearchAction  implementation
 * should be used (see DeleteSearchActionComponent wbo-delete-search-action). Search actions are added to a search grid
 * wrapped in a wbo-actions-search-column column.
 *
 * All search column components must register a provider with a SearchActionSelector directive to allow it to be
 * found within the ActionsSearchColumnComponent. The SearchAction interface must also be implemented.
 *
 * The ActionsSearchColumnComponent will delegate the action button click to the SearchAction#doAction method providing
 * the row data. The resulting Promise is required to perform the action.
 *
 */
@Component({
    selector: "wbo-search-action",
    templateUrl: "./search-action.component.html",
    styleUrls: ["./search-action.component.scss"],
    providers: [
        {provide: SearchActionSelector, useExisting: forwardRef(() => SearchActionComponent), multi: true}
    ]
})
export class SearchActionComponent {
    /**
     * Name of the icon to display
     */
    @Input() icon: string;

    @Input() iconClass: string;

    @Input() iconSize: number = 17;

    /**
     * Name of the action which is used to derive an i18n key
     */
    @Input() title: string;

    /**
     *
     * @returns {number}
     */
    getImportance(): number {
        return 0;
    }

    getIcon(data: any): string {
        return this.icon;
    }

    getIconClass(data: any): string {
        return this.iconClass;
    }

    getIconSize(data: any): number {
        return this.iconSize;
    }

    /**
     *
     * @param data
     * @returns {string}
     */
    getName(data: any): string {
        return this.title;
    }

    /**
     *
     * @param data
     * @returns {Promise<never>}
     */
    doAction(data: any): Promise<void> {
        //
        // This SearchActionComponent should not be used directly.
        // SearchAction implementations should perform the action in the promise
        //
        return Promise.reject("action not implemented");
    }


    isAvailable(data: any): boolean {
        return true;
    }
}
